import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  Download, Printer, Search,
  FileText, Calendar, CheckCircle, AlertCircle
} from 'lucide-react';

function SkeletonRow() {
  return (
    <tr>
      <td><div className="skeleton h-4 w-16" /></td>
      <td><div className="skeleton h-4 w-40" /></td>
      <td><div className="skeleton h-4 w-24 ml-auto" /></td>
      <td><div className="skeleton h-4 w-24 ml-auto" /></td>
    </tr>
  );
}

export default function TrialBalancePage() {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [period, setPeriod] = useState(currentMonth);
  const [adjustment, setAdjustment] = useState('before');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [period, adjustment]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (adjustment === 'after') {
        params.after_adjustment = true;
      }
      const res = await api.get('/reports/trial-balance', { params });
      setData(res.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const formatPeriodLabel = (p) => {
    if (!p) return '';
    const [y, m] = p.split('-');
    const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Neraca Saldo</h1>
          <p className="page-subtitle">Daftar saldo akun (Trial Balance)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => toast.success('Export PDF - Coming Soon')} className="btn btn-outline btn-sm">
            <Download className="h-4 w-4 mr-1.5" /> PDF
          </button>
          <button onClick={() => toast.success('Export Excel - Coming Soon')} className="btn btn-outline btn-sm">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </button>
          <button onClick={() => window.print()} className="btn btn-outline btn-sm">
            <Printer className="h-4 w-4 mr-1.5" /> Cetak
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="label">Periode</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="month"
                  className="input pl-9"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="label">Jenis Neraca Saldo</label>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => setAdjustment('before')}
                  className={`btn btn-sm ${adjustment === 'before' ? 'btn-primary' : 'btn-outline'}`}
                >
                  Sebelum Penyesuaian
                </button>
                <button
                  onClick={() => setAdjustment('after')}
                  className={`btn btn-sm ${adjustment === 'after' ? 'btn-primary' : 'btn-outline'}`}
                >
                  Setelah Penyesuaian
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={fetchData} className="btn btn-primary btn-sm">
                <Search className="h-4 w-4 mr-1.5" /> Muat Ulang
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="card">
          <div className="card-header">
            <div className="skeleton h-5 w-48" />
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Kode</th>
                  <th>Nama Akun</th>
                  <th className="text-right">Debit</th>
                  <th className="text-right">Kredit</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(12)].map((_, i) => <SkeletonRow key={i} />)}
              </tbody>
            </table>
          </div>
        </div>
      ) : !data || !data.accounts || data.accounts.length === 0 ? (
        <div className="card">
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Data neraca saldo tidak tersedia untuk periode ini.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              <FileText className="h-5 w-5 mr-2 inline" />
              {adjustment === 'before' ? 'Neraca Saldo Sebelum Penyesuaian' : 'Neraca Saldo Setelah Penyesuaian'}
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 dark:text-gray-400">{formatPeriodLabel(period)}</span>
              {data.is_balanced != null && (
                <div className={`flex items-center gap-1 text-xs font-medium ${data.is_balanced ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
                  {data.is_balanced ? (
                    <><CheckCircle className="h-3.5 w-3.5" /> Balance Check OK</>
                  ) : (
                    <><AlertCircle className="h-3.5 w-3.5" /> Tidak Balance</>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="table-container rounded-none border-0">
            <table className="table">
              <thead>
                <tr>
                  <th className="w-24">Kode</th>
                  <th>Nama Akun</th>
                  <th className="text-right w-48">Debit</th>
                  <th className="text-right w-48">Kredit</th>
                </tr>
              </thead>
              <tbody>
                {data.accounts.map((acc, idx) => (
                  <tr key={acc.id || idx} className={acc.account_type ? `hover:bg-gray-50 dark:hover:bg-gray-800/30` : ''}>
                    <td>
                      <span className="text-sm font-mono text-gray-600 dark:text-gray-400">{acc.code || acc.account_code || '-'}</span>
                    </td>
                    <td>
                      <span className="text-sm text-gray-700 dark:text-gray-200">{acc.name || acc.account_name || '-'}</span>
                    </td>
                    <td className="text-right">
                      <span className="text-sm text-primary-900 dark:text-primary-400 font-mono">
                        {acc.net_debit > 0 ? formatCurrency(acc.net_debit) : acc.debit > 0 ? formatCurrency(acc.debit) : '-'}
                      </span>
                    </td>
                    <td className="text-right">
                      <span className="text-sm text-danger-700 dark:text-danger-400 font-mono">
                        {acc.net_credit > 0 ? formatCurrency(acc.net_credit) : acc.credit > 0 ? formatCurrency(acc.credit) : '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 dark:bg-gray-800/50 font-semibold border-t-2 border-gray-200 dark:border-gray-600">
                  <td colSpan="2" className="text-sm font-semibold text-gray-900 dark:text-white">
                    Total
                  </td>
                  <td className="text-right">
                    <span className="text-sm font-semibold text-primary-900 dark:text-primary-300">{formatCurrency(data.total_debit)}</span>
                  </td>
                  <td className="text-right">
                    <span className="text-sm font-semibold text-danger-700 dark:text-danger-300">{formatCurrency(data.total_credit)}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Balance validation summary */}
          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Selisih</span>
              <span className={`text-sm font-semibold ${data.is_balanced ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
                {formatCurrency(Math.abs(data.total_debit - data.total_credit))}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">Status:</span>
              {data.is_balanced ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-success-600 dark:text-success-400">
                  <CheckCircle className="h-3 w-3" /> Balance
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-danger-600 dark:text-danger-400">
                  <AlertCircle className="h-3 w-3" /> Tidak Balance
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
